import { Paper as MantinePaper } from "@mantine/core";
import styles from "./paper.module.scss";
import {
    type IProps,
    type Factory,
    type IPaperHeaderProps,
    type PaperHeaderFactory,
    type IPaperHeaderTitleProps,
    type IPaperContentProps,
    type PaperContentFactory,
    type PaperContentHeaderFactory,
    type IPaperContentHeaderProps,
    type IPaperFooterProps,
    type PaperFooterFactory,
} from "./paper.types";
import { componentWrapper, polymorphicComponentWrapper } from "../../utilities";
import { Group } from "../group";
import { Title } from "../title";
import { Stack } from "../stack";
import { Text } from "../text";

/**
 * Paper component for displaying content in a card-like container.
 * @param {React.ReactNode} props.children - Content to display in the paper.
 * @param {string} props.rootStackGap - Gap spacing for the root stack layout.
 * @param {object} props.stackProps - Additional props for the internal Stack component.
 * @returns {JSX.Element} The Paper component.
 */
const Paper = polymorphicComponentWrapper<Factory, IProps>("Paper", ({ children, rootStackGap, ...props }, ref) => {
    const { stackProps, ...otherProps } = props;
    return (
        <MantinePaper
            component="div"
            ref={ref}
            classNames={{ root: styles["container"] }}
            p="lg"
            data-clickable={typeof props.onClick !== "undefined"}
            {...otherProps}
        >
            <Stack h="100%" gap={rootStackGap ?? "xl"} {...stackProps}>
                {children}
            </Stack>
        </MantinePaper>
    );
});

/**
 * Paper header component for displaying header content in a Paper.
 * @returns {JSX.Element} The PaperHeader component.
 */
const PaperHeader = componentWrapper<PaperHeaderFactory, IPaperHeaderProps>("PageHeader", (props, ref) => {
    return <Group ref={ref} justify="space-between" align="center" {...props} />;
});

/**
 * Paper header title component with optional subtitle.
 * @param {string} props.subtitle - Optional subtitle text to display below the title.
 * @returns {JSX.Element} The PaperHeaderTitle component.
 */
const PaperHeaderTitle = ({ subtitle, ...otherProps }: IPaperHeaderTitleProps) => {
    return (
        <Stack gap="xs">
            <Title order={4} {...otherProps} />
            {subtitle && (
                <Text size="xs" dimmed>
                    {subtitle}
                </Text>
            )}
        </Stack>
    );
};

PaperHeader.Title = PaperHeaderTitle;

/**
 * Paper content component for displaying main content in a Paper.
 * @returns {JSX.Element} The PaperContent component.
 */
const PaperContent = componentWrapper<PaperContentFactory, IPaperContentProps>("PaperContent", (props, ref) => {
    return <Stack gap="xxl" pl="lg" ref={ref} {...props} />;
});

const PaperContentHeader = componentWrapper<PaperContentHeaderFactory, IPaperContentHeaderProps>(
    "PaperContentHeader",
    ({ title, subtitle, ...otherProps }, ref) => {
        return (
            <Stack gap="sm" ref={ref} justify="space-between" {...otherProps}>
                <Title order={3}>{title}</Title>
                <Text variant="semibold" size="sm" dimmed>
                    {subtitle}
                </Text>
            </Stack>
        );
    }
);

/**
 * Paper footer component for displaying footer content in a Paper.
 * @returns {JSX.Element} The PaperFooter component.
 */
const PaperFooter = componentWrapper<PaperFooterFactory, IPaperFooterProps>("PaperFooter", (props, ref) => {
    return <Group ref={ref} {...props} />;
});

PaperContent.Header = PaperContentHeader;

Paper.Header = PaperHeader;
Paper.Content = PaperContent;
Paper.Footer = PaperFooter;

export { Paper, PaperHeader, PaperHeaderTitle, PaperContent, PaperContentHeader, PaperFooter };
